/**
 * PhoneInputField — International phone input restricted to G20 countries.
 *
 * - Dialling-code picker = the shared CountryDropdown (flags, search) in `dialCode` mode
 * - Number input field
 * - Stores value in E.164 format e.g. "+912345678901"
 *
 * Usage:
 *   <PhoneInputField
 *     label="Mobile Number"
 *     value={value}
 *     onChange={setValue}
 *   />
 */

import * as React from 'react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { CountryDropdown, G20_COUNTRIES } from '@/components/ui/country-dropdown'
import { cn } from '@/lib/utils'

// ============================================================================
// Helpers
// ============================================================================

const DEFAULT_COUNTRY = 'US'

/** Dialling code for an ISO alpha-2 country, e.g. "IN" → "+91". */
function getCodeForCountry(alpha2: string): string {
  return G20_COUNTRIES.find((c) => c.alpha2 === alpha2)?.countryCallingCodes[0] ?? '+1'
}

/** Parse an E.164 value into { country (alpha-2), number } using G20 codes (longest match first). */
function parseE164(value: string): { country: string; number: string } {
  if (!value || !value.startsWith('+')) return { country: DEFAULT_COUNTRY, number: value || '' }

  // Longest codes first so +1 doesn't swallow +966 etc. The sort is stable and G20_COUNTRIES
  // lists the US first, so the shared +1 code resolves to the United States over Canada.
  const byCodeLength = [...G20_COUNTRIES].sort(
    (a, b) => b.countryCallingCodes[0].length - a.countryCallingCodes[0].length,
  )
  const match = byCodeLength.find((c) => value.startsWith(c.countryCallingCodes[0]))
  if (match) return { country: match.alpha2, number: value.slice(match.countryCallingCodes[0].length) }

  return { country: DEFAULT_COUNTRY, number: value.slice(1) }
}

// ============================================================================
// Props
// ============================================================================

export interface PhoneInputFieldProps {
  label: string
  value: string | undefined
  onChange: (value: string | undefined) => void
  onBlur?: () => void
  required?: boolean
  disabled?: boolean
  error?: string
  placeholder?: string
  className?: string
}

// ============================================================================
// Component
// ============================================================================

export function PhoneInputField({
  label,
  value,
  onChange,
  onBlur,
  required = false,
  disabled = false,
  error,
  placeholder = 'XXX-XXX-XXXX',
  className,
}: PhoneInputFieldProps) {
  const parsed = parseE164(value || '')
  const [country, setCountry] = React.useState(parsed.country)
  const [number, setNumber] = React.useState(parsed.number)

  // Sync external value changes (e.g. form reset / initialValues).
  // If the incoming value still matches the currently-selected country's code,
  // keep that choice — this preserves a US vs Canada (+1) selection the user made
  // instead of re-deriving it (which would always collapse +1 to one country).
  React.useEffect(() => {
    const currentCode = getCodeForCountry(country)
    if (value && value.startsWith(currentCode)) {
      setNumber(value.slice(currentCode.length))
      return
    }
    const p = parseE164(value || '')
    setCountry(p.country)
    setNumber(p.number)
  }, [value])

  const emit = (alpha2: string, num: string) => {
    onChange(num ? `${getCodeForCountry(alpha2)}${num}` : undefined)
  }

  const handleCountryChange = (alpha2: string) => {
    setCountry(alpha2)
    emit(alpha2, number)
  }

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const num = e.target.value.replace(/\D/g, '')
    setNumber(num)
    emit(country, num)
  }

  return (
    <div className={cn('space-y-2', className)}>
      <Label>
        {label}
        {required && <span className="text-destructive ml-1">*</span>}
      </Label>

      {/* One bordered box styled like the shared Input; the picker and number field sit inside it. */}
      <div
        className={cn(
          'flex h-10 rounded-lg border-2 border-input bg-background/50 shadow-sm transition-all duration-200',
          'hover:border-primary/30 focus-within:border-primary focus-within:ring-4 focus-within:ring-primary/10',
          disabled && 'opacity-50 cursor-not-allowed',
          error && 'border-destructive focus-within:border-destructive focus-within:ring-destructive/10',
        )}
      >
        <CountryDropdown
          dialCode
          options={G20_COUNTRIES}
          value={country}
          onChange={(c) => handleCountryChange(c.alpha2)}
          disabled={disabled}
          className="space-y-0"
          triggerClassName="h-full w-auto shrink-0 rounded-r-none rounded-l-md border-0 border-r-2 bg-transparent pl-3 pr-2 shadow-none hover:bg-muted/50 focus:border-input focus:ring-0"
        />

        <Input
          type="tel"
          value={number}
          onChange={handleNumberChange}
          onBlur={onBlur}
          placeholder={placeholder}
          disabled={disabled}
          className="h-full rounded-none border-0 bg-transparent px-3 shadow-none hover:bg-transparent focus-visible:bg-transparent focus-visible:ring-0"
        />
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  )
}
